/**
 * Полоса прогресса папки эфира в редакторе переводчика (W4.4, фаза 4) — данные и тексты, без React.
 *
 * Источник — translate-python `GET /stream_editor/v1/progress?media=` (переводчику из списка): по папке и языку
 * четыре шага «русский — перевод — голос — в эфире» и «зрители увидят в ЧЧ:ММ» (начало речи папки + задержка
 * зрителей), правки переводчика по папке (заявки полосы правок site_server и их отчёты).
 */
import { fixCueCounts } from "../../../../../utils/pluralRu";

export type StepState =
    | "done"
    | "running"
    | "queued"
    | "failed"
    | "held"
    | "degraded"
    | "updating"
    | "pending";

export interface StreamFix {
    id: string;
    state: string;
    stage?: string;
    kind?: string;
    found?: string;
    /** Что сделал редактор в заявке `text_changed` (site_server `EDITOR_EDITS`): revoice — ♪, text — текст… */
    edit?: string;
    folder?: number;
    lang?: string;
    cue_ids?: string[];
    by?: string;
    requested_at?: string;
    settle_until?: string;
    subs_published_at?: string | null;
    published_at?: string | null;
    public_at?: string | null;
    to_record_only?: boolean | null;
    hls_revision?: string | null;
    error?: string | null;
    summary_ru?: string;
    finished_at?: string;
}

export interface ProgressLang {
    stage?: string;
    state?: string;
    degraded?: boolean;
    published_at?: string | null;
    steps: Record<string, StepState> & { current?: string; title?: string };
    fixes: StreamFix[];
    fixes_open: number;
}

export interface ProgressFolder {
    folder: number;
    closed_at?: string | null;
    air_start_at?: string | null;
    public_at?: string | null;
    langs: Record<string, ProgressLang>;
    ru_sync?: { status?: string; mode?: string; languages?: Record<string, { status?: string; error?: string }> };
}

export interface StreamProgressData {
    media: string;
    open?: boolean;
    closed?: boolean;
    strict?: boolean;
    held?: boolean;
    held_folders?: number[];
    viewer_delay_sec?: number | null;
    steps: { id: string; title: string }[];
    folders: ProgressFolder[];
    fixes_error?: string;
}

export const STEP_TITLES: Record<string, string> = {
    ru: "русский",
    translate: "перевод",
    voice: "голос",
    air: "в эфире",
};

export const STATE_MARK: Record<StepState, string> = {
    done: "✓",
    running: "⟳",
    queued: "…",
    failed: "✕",
    held: "⏸",
    degraded: "!",
    updating: "⟳",
    pending: "·",
};

export const STATE_COLOR: Record<StepState, string> = {
    done: "green",
    running: "processing",
    queued: "default",
    failed: "error",
    held: "warning",
    degraded: "warning",
    updating: "processing",
    pending: "default",
};

export const OPEN_FIX_STATES = ["queued", "settling", "running"];
/**
 * `needs_translator` (п.50г, 02.11): правка не влезла и временем, а соседние реплики без согласия переводчика не
 * сокращаются — круг откатил папку (в эфире прежняя версия) и спрашивает: сократить свой текст или разрешить сократить
 * соседнюю. Вопрос — в `summary_ru` отчёта; ответить — здесь новой правкой или в плеере («Мои правки»).
 */
export const ASK_FIX_STATE = "needs_translator";
export const FINAL_FIX_STATES = ["fixed", "failed", "nothing_to_fix", "refused", ASK_FIX_STATE];

/** Язык пары в ответе: редактор зовёт китайский «ch», эфир — «zh» (и наоборот). */
export const langOf = (folder: ProgressFolder | undefined, lang: string): ProgressLang | undefined => {
    if (!folder) return undefined;
    const aliases = lang === "ch" ? ["ch", "zh"] : lang === "zh" ? ["zh", "ch"] : [lang];
    for (const name of aliases) {
        if (folder.langs?.[name]) return folder.langs[name];
    }
    return undefined;
};

/** ЧЧ:ММ местного времени. */
export const hhmm = (iso?: string | null): string | null => {
    if (!iso) return null;
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return null;
    return `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
};

/** «зрители увидят в ЧЧ:ММ» / «зрители уже видят (с ЧЧ:ММ)». */
export const viewersLine = (publicAt?: string | null, now: number = Date.now()): string | null => {
    const at = hhmm(publicAt);
    if (!at || !publicAt) return null;
    return new Date(publicAt).getTime() > now ? `зрители увидят в ${at}` : `зрители видят с ${at}`;
};

const ISO_TIME = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?/g;

/** Время отчёта сервера (ISO) — ЧЧ:ММ местного времени: «в эфире: 18:20; зрители увидят: 18:40». */
export const localTimes = (text: string): string => text.replace(ISO_TIME, (iso) => hhmm(iso) || iso);

/**
 * Тост «Правка принята»: что уйдёт в эфир. У ♪ — новый дубль, а не «перевод и голос» (проба P50m10d, E6: ♪ — это
 * переозвучка, текст не менялся).
 */
export const acceptedLine = (fix: StreamFix): string => {
    const what = fix.found || fix.kind || "правка";
    switch (fix.edit) {
        case "revoice":
            return `${what}: уйдёт в эфир сама — новый дубль реплики, новая ревизия`;
        case "skip":
        case "unskip":
        case "delete_audio":
            return `${what}: уйдёт в эфир сама — звук папки без пересинтеза, новая ревизия`;
        case "time":
            // Правка времени (liveTiming.ts, проба P50m10d): соседей подгоняет временем и сверяет с лектором сервер.
            return `${what}: уйдёт в эфир сама — новое место реплики, соседей подгонит временем круг правки, новая ревизия`;
        default:
            return `${what}: уйдёт в эфир сама — перевод и голос правленых реплик, новая ревизия`;
    }
};

/** Отчёт правки одной строкой для тоста. */
export const fixLine = (fix: StreamFix): string => {
    // Отчёт сервера, написанный до исправления склонения, — «(1 реплик)» склоняется здесь (P50m10).
    if (fix.summary_ru) return localTimes(fixCueCounts(fix.summary_ru));
    const what = fix.found || fix.kind || "правка";
    if (fix.state === "fixed") {
        const when = fix.to_record_only
            ? "уже показанная часть — исправлено в записи"
            : viewersLine(fix.public_at) || "в эфире";
        return `${what}: в эфире ${hhmm(fix.published_at || fix.subs_published_at) || ""} — ${when}`.trim();
    }
    if (fix.state === "refused" || fix.state === "failed") return `${what}: ${fix.error || fix.state}`;
    if (fix.state === "nothing_to_fix") return `${what}: менять нечего`;
    return `${what}: ${fix.stage || fix.state}`;
};

export interface FixEvent {
    kind: "accepted" | "finished";
    fix: StreamFix;
}

/**
 * Что сказать тостом: новые заявки этого переводчика («принята») и те, что дошли до итога. `seen` — что уже
 * сказано (id → состояние), меняется на месте. Первый проход (`seen` пуст) только запоминает: старые правки
 * при открытии редактора не всплывают.
 */
export const fixEvents = (
    fixes: StreamFix[],
    seen: Map<string, string>,
    me: string | null,
    firstPass: boolean,
): FixEvent[] => {
    const out: FixEvent[] = [];
    for (const fix of fixes) {
        if (!fix?.id) continue;
        if (me && fix.by && fix.by.toLowerCase() !== me.toLowerCase()) continue;
        const before = seen.get(fix.id);
        seen.set(fix.id, fix.state);
        if (firstPass) continue;
        if (before === undefined) {
            out.push({ kind: FINAL_FIX_STATES.includes(fix.state) ? "finished" : "accepted", fix });
        } else if (before !== fix.state && FINAL_FIX_STATES.includes(fix.state)) {
            out.push({ kind: "finished", fix });
        }
    }
    return out;
};

/** Чьё: заявки этого переводчика (без адреса — все). */
const isMine = (fix: StreamFix, me: string | null): boolean =>
    !me || !fix.by || fix.by.toLowerCase() === me.toLowerCase();

/** Правки этого переводчика по папке и языку, что ещё идут (их ход видно после «Готово — в эфир»). */
export const openOwnFixIds = (fixes: StreamFix[], me: string | null): string[] =>
    fixes.filter((fix) => fix?.id && isMine(fix, me) && OPEN_FIX_STATES.includes(fix.state)).map((fix) => fix.id);

export type OwnFixPhase = "none" | "accepted" | "air" | "record" | "nothing" | "failed" | "ask";

export interface OwnFixStatus {
    phase: OwnFixPhase;
    /** Коротко, рядом с кнопкой. */
    text: string;
    /** Подсказка (title). */
    title: string;
}

/**
 * Ход своей правки после «Готово — в эфир»: по заявкам, что ушли этим нажатием (`ids`), и опросу прогресса.
 * «принята» → «в эфире · зрители увидят ЧЧ:ММ»; по-русски, без машинных fixed/refused.
 */
export const ownFixStatus = (fixes: StreamFix[], ids: string[], now: number = Date.now()): OwnFixStatus => {
    if (!ids.length) {
        return { phase: "none", text: "правок нет", title: "Ждущих правок нет — в эфире всё как есть" };
    }
    const byId = new Map(fixes.filter((fix) => fix?.id).map((fix) => [fix.id, fix]));
    const mine = ids.map((id) => byId.get(id));
    if (mine.some((fix) => !fix || !FINAL_FIX_STATES.includes(fix.state))) {
        return {
            phase: "accepted",
            text: "принята",
            title: "Правка принята: перевод и голос правленых реплик уйдут в эфир сами",
        };
    }
    const done = mine as StreamFix[];
    const asked = done.find((fix) => fix.state === ASK_FIX_STATE);
    if (asked) {
        return {
            phase: "ask",
            text: "⚠ не влезает — сократите текст или разрешите сократить соседнюю (плеер, «Мои правки»)",
            title: fixLine(asked),
        };
    }
    const bad = done.find((fix) => fix.state === "failed" || fix.state === "refused");
    if (bad) return { phase: "failed", text: "✕ ошибка: не вышла в эфир", title: fixLine(bad) };
    const fixed = done.filter((fix) => fix.state === "fixed");
    if (!fixed.length) return { phase: "nothing", text: "менять нечего", title: fixLine(done[0]) };
    if (fixed.every((fix) => fix.to_record_only)) {
        return { phase: "record", text: "✓ исправлено в записи", title: "Эту часть зрители уже видели — исправлено в записи" };
    }
    const publicAt = fixed
        .map((fix) => fix.public_at)
        .filter((at): at is string => Boolean(at) && !Number.isNaN(new Date(at as string).getTime()))
        .sort()
        .pop();
    const viewers = viewersLine(publicAt, now);
    return {
        phase: "air",
        text: viewers ? `✓ в эфире · ${viewers}` : "✓ в эфире",
        title: fixed.map(fixLine).join("\n"),
    };
};
